import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionSummary, Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BriaSlider from "../../../../../components/common/BriaSlider/BriaSlider";
import ConfigSwitch from "../../../../../components/common/BriaSwitch/Config/ConfigSwitch";
import LinkConstants from "../../../../../constants/LinkConstants.ts";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import { tgAdvancedConfig } from "../../../../NewTG/App/Image/Config/TgImageGenerationConfig";
import styles from "../TextToImageConfig.module.scss";
import TextToImageSeed from "./Seed/TextToImageSeed";
import TextToImageSteps from "./Steps/TextToImageSteps";
import TextToImageEnhanceSwitch from "./TextToImageEnhanceSwitch.tsx/TextToImageEnhanceSwitch.tsx";
import TextToImageNegativePrompt from "./TextToImageNegativePrompt/TextToImageNegativePrompt.tsx";

interface TextToImageAdvancedProps {
	hideSteps?: boolean;
}

const TextToImageAdvanced = ({ hideSteps = false }: TextToImageAdvancedProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.config" });
	const { textToImageStore, imageToImageStore } = useAppStore();
	const { config, handleConfigChange } = textToImageStore;
	const maxNumResults =
		(config.tailored_model_id
			? tgAdvancedConfig.max_results
			: textToImageStore.models[config.model]?.max_results) || 4;

	const handleNumResultsChange = (_event: any, numResults: number | number[]) => {
		handleConfigChange("num_results", numResults as number);
	};

	const handleContentModerationChange = () => {
		handleConfigChange("content_moderation", !textToImageStore.config?.content_moderation);
		imageToImageStore.handleConfigChange(
			"content_moderation",
			!(imageToImageStore.config?.content_moderation ?? false),
		);
	};

	return (
		<Accordion elevation={0} disableGutters TransitionProps={{ timeout: 200 }} className={styles.advancedAccordion}>
			<AccordionSummary color="initial" expandIcon={<ExpandMore />} className={styles.accordionSummary}>
				{t("advanced")}
			</AccordionSummary>

			<Box className={styles.advanced}>
				{!iframeStore.isIframe() && (
					<ConfigSwitch
						label={t("contentModeration.label")}
						info={`${t("contentModeration.tooltip.info")} <a href="${
							LinkConstants.CONTENT_MODERATION_BRIA_API_DOC
						}" target="_blank" rel="noopener noreferrer">Learn more</a>`}
						checked={textToImageStore.config?.content_moderation ?? false}
						onChange={handleContentModerationChange}
					/>
				)}
				<TextToImageNegativePrompt />
				<InputLayout
					label={t("numberOfImages.label")}
					labelClassName={styles.inputLabel}
					info={t("numberOfImages.info")}
				>
					<BriaSlider
						value={config.num_results}
						onChange={handleNumResultsChange}
						step={1}
						min={1}
						max={maxNumResults}
						marks={Array.from({ length: maxNumResults }, (_, index) => ({
							value: index + 1,
							label: `${index + 1}`,
						}))}
					/>
				</InputLayout>
				{!textToImageStore.isTextToVector() && <TextToImageEnhanceSwitch />}

				{!hideSteps && <TextToImageSteps />}

				<TextToImageSeed />
			</Box>
		</Accordion>
	);
};

export default observer(TextToImageAdvanced);
